import { createLightTheme } from 'baseui';
import type * as IconTypes from 'baseui/icon';
import { ChevronLeft, ChevronRight } from 'baseui/icon';
import { Theme as BwTheme, ThemePrimitives } from 'baseui/theme';
import { getLuminance, meetsContrastGuidelines, rgba, setLightness } from 'polished';
import { HeThemeParameters } from '../common/services/theme/HeThemeParameters.type';
import { ColourScale, HeColours } from '../common/styles/HeColours';
import { FONT_LIST } from '../common/styles/constants';

const DEFAULT_SHADE = 400;
const DEFAULT_PRIMARY_SHADE = 500;

export const buildTheme = (params: HeThemeParameters): BwTheme => {
  const primitives = buildPrimitives(params);
  return createLightTheme(primitives, buildOverrides(primitives, params));
};

const buildPrimitives = (params: HeThemeParameters): ThemePrimitives => {
  const negativeScale = HeColours.red;
  const positiveScale = HeColours.green;
  const warningScale = HeColours.yellow;
  const monoScale = HeColours.gray;

  const primaryScale = params.primaryColour
    ? buildBrandScale(params.primaryColour)
    : HeColours.teal;

  const primaryKey = params.primaryColour ?? primaryScale[DEFAULT_PRIMARY_SHADE];

  const accentScale = params.secondaryColour
    ? buildBrandScale(params.secondaryColour)
    : HeColours.orange;

  const accentKey = params.secondaryColour ?? accentScale[DEFAULT_SHADE];

  return {
    primaryA: HeColours.black,
    primaryB: HeColours.white,

    negative: negativeScale[DEFAULT_SHADE],
    negative50: negativeScale[50],
    negative100: negativeScale[100],
    negative200: negativeScale[200],
    negative300: negativeScale[300],
    negative400: negativeScale[400],
    negative500: negativeScale[500],
    negative600: negativeScale[600],
    negative700: negativeScale[700],

    positive: positiveScale[DEFAULT_SHADE],
    positive50: positiveScale[50],
    positive100: positiveScale[100],
    positive200: positiveScale[200],
    positive300: positiveScale[300],
    positive400: positiveScale[400],
    positive500: positiveScale[500],
    positive600: positiveScale[600],
    positive700: positiveScale[700],

    warning: warningScale[DEFAULT_SHADE],
    warning50: warningScale[50],
    warning100: warningScale[100],
    warning200: warningScale[200],
    warning300: warningScale[300],
    warning400: warningScale[400],
    warning500: warningScale[500],
    warning600: warningScale[600],
    warning700: warningScale[700],

    accent: accentKey,
    accent50: accentScale[50],
    accent100: accentScale[100],
    accent200: accentScale[200],
    accent300: accentScale[300],
    accent400: accentScale[400],
    accent500: accentScale[500],
    accent600: accentScale[600],
    accent700: accentScale[700],

    primary: primaryKey,
    primary50: primaryScale[50],
    primary100: primaryScale[100],
    primary200: primaryScale[200],
    primary300: primaryScale[300],
    primary400: primaryScale[400],
    primary500: primaryScale[500],
    primary600: primaryScale[600],
    primary700: primaryScale[700],

    mono100: monoScale[100],
    mono200: monoScale[200],
    mono300: monoScale[300],
    mono400: monoScale[400],
    mono500: monoScale[500],
    mono600: monoScale[600],
    mono700: monoScale[700],
    mono800: monoScale[800],
    mono900: monoScale[900],
    mono1000: monoScale[1000],

    rating200: warningScale[200],
    rating400: warningScale[400],

    primaryFontFamily: FONT_LIST,
  };
};

const buildOverrides = (
  primitives: ThemePrimitives,
  params: HeThemeParameters,
): BwThemeOverrides => ({
  colors: {
    borderFocus: primitives.primary,

    buttonPrimaryText:
      params.primaryContrastTextColour ?? selectContrastColour(params.primaryColour),

    buttonSecondaryFill: primitives.accent,
    buttonSecondaryHover: primitives.accent700,
    buttonSecondaryActive: primitives.accent600,
    buttonSecondaryText:
      params.secondaryContrastTextColour ?? selectContrastColour(params.secondaryColour),

    // Baseweb uses the accent colour for 'info' notifications, we prefer blue
    notificationInfoBackground: HeColours.blue[50],
    notificationInfoText: HeColours.blue[600],
    // Baseweb uses the 500 shade for notification text; we prefer a better contrast
    notificationNegativeText: primitives.negative600,
    notificationPositiveText: primitives.positive600,
    notificationWarningText: primitives.warning600,
    // We like the warning background to pop a bit
    notificationWarningBackground: primitives.warning100,

    inputFill: primitives.primaryB,
  },
  icons: {
    ArrowLeft: ChevronLeft,
    ArrowRight: ChevronRight,
  },
  borders: {
    inputBorderRadius: '4px',
  },
  typography: {
    DisplayMedium: {},
  },
  lighting: {
    shadow400: `0 1px 10px ${rgba(HeColours.gray[500], 0.15)}`,
    shadow500: `0 1px 15px ${rgba(HeColours.gray[500], 0.15)}`,
    shadow600: `0 1px 20px ${rgba(HeColours.gray[500], 0.2)}`,
    shadow700: `0 1px 30px ${rgba(HeColours.gray[500], 0.3)}`,
  },
});

const selectContrastColour = (colour?: string) => {
  if (!colour) return HeColours.white;
  return meetsContrastGuidelines(HeColours.white, colour).AA ? HeColours.white : HeColours.black;
};

const buildBrandScale = (baseColour: string): ColourScale => ({
  50: standardLuminance(baseColour, 50),
  100: standardLuminance(baseColour, 100),
  200: standardLuminance(baseColour, 200),
  300: standardLuminance(baseColour, 300),
  400: standardLuminance(baseColour, 400),
  500: standardLuminance(baseColour, 500),
  600: standardLuminance(baseColour, 600),
  700: standardLuminance(baseColour, 700),
});

const standardLuminance = (colour: string, key: keyof ColourScale) =>
  setLightness(getLuminance(HeColours.blue[key]), colour);

interface BwThemeOverrides {
  animation?: Partial<BwTheme['animation']>;
  borders?: DeeperPartial<BwTheme['borders']>;
  breakpoints?: Partial<BwTheme['breakpoints']>;
  colors?: Partial<BwTheme['colors']>;
  direction?: BwTheme['direction'];
  grid?: Partial<BwTheme['grid']>;
  icons?: Partial<typeof IconTypes>;
  lighting?: Partial<BwTheme['lighting']>;
  mediaQuery?: Partial<BwTheme['mediaQuery']>;
  name?: BwTheme['name'];
  sizing?: Partial<BwTheme['sizing']>;
  typography?: DeeperPartial<BwTheme['typography']>;
  zIndex?: Partial<BwTheme['zIndex']>;
}

type DeeperPartial<T> = { [Key in keyof T]?: Partial<T[Key]> };
